import {
  type Academy,
  TierFeatureKey,
  canAccessFeature,
  normalizeSubscriptionTier,
  type AcademySport,
  type Lesson,
  isStarterRequestBookingAcademy,
} from '@court-sync/shared';
import { resolvePrimaryAcademySport, getActiveAcademySports } from '@/lib/sportPresentation';
import type { PublicOrgModules } from '@/services/publicOrg';

export { isStarterRequestBookingAcademy };

/** Member login, register, and guardian dashboards are unavailable on Starter. */
export function isStarterMemberAuthBlockedAcademy(
  academy: Pick<Academy, 'subscriptionTier'>
): boolean {
  const tier = normalizeSubscriptionTier(academy.subscriptionTier ?? null) ?? 'starter';
  return !canAccessFeature(tier, TierFeatureKey.STUDENT_GUARDIAN_PORTAL);
}

/**
 * Public org header sign-in is shown when visitors can use member portals:
 * club online join, or academy Professional+ student/guardian and coach accounts.
 */
export function isOrgPublicSignInAvailable(
  modules: Pick<PublicOrgModules, 'membershipOnlineJoinEnabled' | 'hasAcademy'>,
  academy: Pick<Academy, 'subscriptionTier'> | null | undefined
): boolean {
  if (modules.membershipOnlineJoinEnabled) return true;
  if (!modules.hasAcademy || !academy) return false;

  const tier = normalizeSubscriptionTier(academy.subscriptionTier ?? null) ?? 'starter';
  return (
    canAccessFeature(tier, TierFeatureKey.STUDENT_GUARDIAN_PORTAL) &&
    canAccessFeature(tier, TierFeatureKey.COACH_PORTAL)
  );
}

export function resolvePublicSportFilterId(
  searchParams: URLSearchParams,
  academySportIds: readonly string[]
): string | null {
  const sportParam = searchParams.get('sport');
  if (!sportParam || sportParam === 'all') return null;
  return academySportIds.includes(sportParam) ? sportParam : null;
}

export function lessonMatchesSportFilter(
  lesson: Pick<Lesson, 'academySportId'>,
  filterAcademySportId: string | null,
  academySports: readonly AcademySport[]
): boolean {
  if (!filterAcademySportId) return true;
  const active = getActiveAcademySports(academySports);
  const lessonSportId =
    lesson.academySportId ?? resolvePrimaryAcademySport(active)?.id ?? null;
  if (!lessonSportId) return true;
  return lessonSportId === filterAcademySportId;
}
